import { Autocomplete, AutocompleteRenderInputParams, TextField, Typography } from '@mui/material';
import { DeviceSensor, DeviceSensorType, MutateWidgetPayload } from '@thingslog/repositories';
import { t } from 'i18next';
import React, { FC } from 'react';
import { Control, Controller, ControllerFieldState, ControllerRenderProps } from 'react-hook-form';
import {
  analogSensorTypeOptions,
  digitalSensorTypeOptions,
} from '../../../../../sensorsAndNetworkConfig/common/SensorTypeOptions';
import {
  AddQueryRowButton,
  BatteryRangeField,
  DeleteQueryRowButton,
  PreferFlowField,
  QueryMultiSelectField,
  QuerySection,
  QuerySingleSelectField,
  SignalMetricsField,
  SignalRangeField,
  TimespanFields,
  TransmissionsMetricsField,
} from './QuerySettingsFields';
import {
  QuerySelectOption,
  QuerySettingsFieldsProps,
  QuerySettingsOptionSources,
  WidgetFieldPath,
  WidgetRegisterOptions,
} from './models';

interface DeviceSensorRowsFieldProps {
  control: Control<MutateWidgetPayload>;
  optionSources: QuerySettingsOptionSources;
  disabled?: boolean;
  optional?: boolean;
}

interface DeviceSensorTypeRowsFieldProps {
  control: Control<MutateWidgetPayload>;
  optionSources: QuerySettingsOptionSources;
  disabled?: boolean;
  optional?: boolean;
}

interface SharedTailFieldsProps {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  timespanType: QuerySettingsFieldsProps['timespanType'];
  handleTimespanTypeChange: QuerySettingsFieldsProps['handleTimespanTypeChange'];
}

const sensorTypeOptions = [
  ...Object.entries(analogSensorTypeOptions).map(
    ([value, label]: [string, string]): { value: string; label: string } => ({
      value,
      label,
    })
  ),
  ...Object.entries(digitalSensorTypeOptions).map(
    ([value, label]: [string, string]): { value: string; label: string } => ({
      value,
      label,
    })
  ),
];

const requiredArrayRules = (message: string, optional?: boolean): WidgetRegisterOptions =>
  optional
    ? {}
    : {
        validate: (value: unknown): string | true => {
          if (!Array.isArray(value) || value.length === 0) {
            return message;
          }

          return true;
        },
      };

export const ApiAlarmsCountQueryForm: FC<QuerySettingsFieldsProps> = ({
  control,
  disabled = false,
  timespanType,
  handleTimespanTypeChange,
}: QuerySettingsFieldsProps) => (
  <TimespanFields
    control={control}
    disabled={disabled}
    timespanType={timespanType}
    handleTimespanTypeChange={handleTimespanTypeChange}
  />
);

export const ApiBatteryHistogramQueryForm: FC<QuerySettingsFieldsProps> = () => null;

export const ApiSignalHistogramQueryForm: FC<QuerySettingsFieldsProps> = () => null;

const getOptionForValue = (
  value: string | null | undefined,
  options: QuerySelectOption[]
): QuerySelectOption | null => {
  if (!value) {
    return null;
  }

  return (
    options.find((option: QuerySelectOption): boolean => option.value === value) ?? {
      value,
      label: value,
      searchText: value.toLowerCase(),
    }
  );
};

const filterOptions = (
  options: QuerySelectOption[],
  { inputValue }: { inputValue: string }
): QuerySelectOption[] => {
  const searchTerm = inputValue.toLowerCase();

  return options.filter((option: QuerySelectOption): boolean =>
    option.searchText.includes(searchTerm)
  );
};

const OptionSelect: FC<{
  value: string;
  label: string;
  options: QuerySelectOption[];
  disabled?: boolean;
  loading?: boolean;
  tourId?: string;
  onChange: (value: string) => void;
}> = ({
  value,
  label,
  options,
  disabled = false,
  loading = false,
  tourId,
  onChange,
}: {
  value: string;
  label: string;
  options: QuerySelectOption[];
  disabled?: boolean;
  loading?: boolean;
  tourId?: string;
  onChange: (value: string) => void;
}) => (
  <div data-dashboard-tour={tourId}>
    <Autocomplete<QuerySelectOption, false, false, false>
      value={getOptionForValue(value, options)}
      options={options}
      filterOptions={filterOptions}
      getOptionLabel={(option: QuerySelectOption): string => option.label}
      isOptionEqualToValue={(option: QuerySelectOption, selected: QuerySelectOption): boolean =>
        option.value === selected.value
      }
      disabled={disabled || loading}
      loading={loading}
      onChange={(_: React.SyntheticEvent, selected: QuerySelectOption | null): void => {
        onChange(selected?.value ?? '');
      }}
      renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
        <TextField {...params} label={label} size="small" />
      )}
    />
  </div>
);

const DeviceSensorTypeRowsField: FC<DeviceSensorTypeRowsFieldProps> = ({
  control,
  optionSources,
  disabled = false,
  optional = false,
}: DeviceSensorTypeRowsFieldProps) => (
  <Controller
    name={'settings.query.deviceSensorTypes' as WidgetFieldPath}
    control={control}
    rules={requiredArrayRules(
      t('dashboard_add_device_sensor_type_required', 'Add at least one device sensor type'),
      optional
    )}
    render={({
      field,
      fieldState,
    }: {
      field: ControllerRenderProps<MutateWidgetPayload, WidgetFieldPath>;
      fieldState: ControllerFieldState;
    }): JSX.Element => {
      const rows = (Array.isArray(field.value) ? field.value : []) as DeviceSensorType[];

      const updateRow = (
        index: number,
        key: keyof DeviceSensorType,
        value: DeviceSensorType[keyof DeviceSensorType]
      ): void => {
        field.onChange(
          rows.map((row: DeviceSensorType, rowIndex: number): DeviceSensorType => {
            if (rowIndex !== index) {
              return row;
            }

            return {
              ...row,
              [key]: value,
            };
          })
        );
      };

      return (
        <QuerySection
          title={t('dashboard_device_sensor_types', 'Device Sensor Types')}
          tourId="query-device-type-list"
          description={
            optional
              ? t(
                  'dashboard_device_sensor_types_optional_description',
                  'Optional groups of devices matched by sensor type.'
                )
              : t(
                  'dashboard_device_sensor_types_description',
                  'Select devices and the sensor type that should be read from them.'
                )
          }
        >
          <div className="flex flex-col gap-3">
            {rows.map((row: DeviceSensorType, index: number) => (
              <div
                key={`${row.sensorType || 'sensor-type'}-${index}`}
                className="grid grid-cols-[1fr_auto] gap-2 rounded-md border border-solid border-gray-200 p-2 md:grid-cols-[1fr_1fr_auto]"
              >
                <OptionMultiSelect
                  values={row.deviceNumbers ?? []}
                  label={t('devices', 'Devices')}
                  tourId="query-device-type-devices"
                  options={optionSources.multiDeviceOptions}
                  loading={optionSources.deviceOptionsLoading}
                  disabled={disabled}
                  onChange={(values: string[]): void => {
                    updateRow(index, 'deviceNumbers', values);
                  }}
                />
                <div data-dashboard-tour="query-device-type-sensor-type">
                  <Autocomplete<string, false, false, true>
                    freeSolo
                    value={row.sensorType ?? ''}
                    options={sensorTypeOptions.map(
                      (option: { value: string; label: string }): string => option.value
                    )}
                    disabled={disabled}
                    onChange={(_: React.SyntheticEvent, value: string | null): void => {
                      updateRow(index, 'sensorType', value ?? '');
                    }}
                    onInputChange={(_: React.SyntheticEvent, value: string): void => {
                      updateRow(index, 'sensorType', value);
                    }}
                    renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                      <TextField
                        {...params}
                        label={t('dashboard_sensor_type', 'Sensor Type')}
                        size="small"
                      />
                    )}
                  />
                </div>
                <DeleteQueryRowButton
                  label={t('dashboard_remove_sensor_type', 'Remove sensor type')}
                  disabled={disabled}
                  onClick={(): void => {
                    field.onChange(
                      rows.filter(
                        (_: DeviceSensorType, rowIndex: number): boolean => rowIndex !== index
                      )
                    );
                  }}
                />
              </div>
            ))}

            <AddQueryRowButton
              label={t('dashboard_add_sensor_type', 'Add Sensor Type')}
              disabled={disabled}
              tourId="query-device-type-add"
              onClick={(): void => {
                field.onChange([...rows, { deviceNumbers: [], sensorType: '' }]);
              }}
            />

            {fieldState.error && (
              <Typography color="error" variant="caption">
                {fieldState.error.message}
              </Typography>
            )}
          </div>
        </QuerySection>
      );
    }}
  />
);

export const ApiDeviceSensorQueryForm: FC<QuerySettingsFieldsProps> = ({
  control,
  disabled = false,
  optionSources,
  timespanType,
  handleTimespanTypeChange,
}: QuerySettingsFieldsProps) => (
  <>
    <QuerySection
      title={t('dashboard_device_sensor', 'Device Sensor')}
      description={t('dashboard_device_sensor_description', 'Read one sensor from one device.')}
      tourId="query-single-device-sensor"
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <QuerySingleSelectField
          control={control}
          name={'settings.query.deviceNumber' as WidgetFieldPath}
          label={t('drawer_device', 'Device')}
          tourId="query-single-device"
          options={optionSources.singleDeviceOptions}
          loading={optionSources.deviceOptionsLoading}
          disabled={disabled}
          rules={{ required: t('dashboard_device_required', 'Device is required') }}
        />
        <QuerySingleSelectField
          control={control}
          name={'settings.query.sensorIndex' as WidgetFieldPath}
          label={t('sensor', 'Sensor')}
          tourId="query-single-sensor"
          options={optionSources.sensorIndexOptions}
          disabled={disabled}
          rules={{ required: t('dashboard_sensor_required', 'Sensor is required') }}
        />
      </div>
    </QuerySection>
    <TimespanFields
      control={control}
      disabled={disabled}
      timespanType={timespanType}
      handleTimespanTypeChange={handleTimespanTypeChange}
    />
  </>
);

export const ApiDevicesBatteryQueryForm: FC<QuerySettingsFieldsProps> = ({
  control,
  disabled = false,
  optionSources,
}: QuerySettingsFieldsProps) => (
  <>
    <QuerySection
      title={t('devices', 'Devices')}
      description={t(
        'dashboard_devices_optional_description',
        'Pick devices to include. Leave empty to include all devices.'
      )}
    >
      <QueryMultiSelectField
        control={control}
        name={'settings.query.specificDevices' as WidgetFieldPath}
        label={t('devices', 'Devices')}
        tourId="query-specific-devices"
        options={optionSources.multiDeviceOptions}
        loading={optionSources.deviceOptionsLoading}
        disabled={disabled}
        emptyText={t(
          'dashboard_no_devices_selected_uses_all',
          'No devices selected. The widget will use all devices.'
        )}
      />
    </QuerySection>
    <BatteryRangeField control={control} disabled={disabled} />
  </>
);

export const ApiDevicesSignalQueryForm: FC<QuerySettingsFieldsProps> = ({
  control,
  disabled = false,
  optionSources,
}: QuerySettingsFieldsProps) => (
  <>
    <QuerySection
      title={t('devices', 'Devices')}
      description={t(
        'dashboard_devices_optional_description',
        'Pick devices to include. Leave empty to include all devices.'
      )}
    >
      <QueryMultiSelectField
        control={control}
        name={'settings.query.specificDevices' as WidgetFieldPath}
        label={t('devices', 'Devices')}
        tourId="query-specific-devices"
        options={optionSources.multiDeviceOptions}
        loading={optionSources.deviceOptionsLoading}
        disabled={disabled}
        emptyText={t(
          'dashboard_no_devices_selected_uses_all',
          'No devices selected. The widget will use all devices.'
        )}
      />
    </QuerySection>
    <SignalRangeField control={control} disabled={disabled} />
    <SignalMetricsField control={control} disabled={disabled} />
  </>
);

export const ApiDevicesTransmissionsQueryForm: FC<QuerySettingsFieldsProps> = ({
  control,
  disabled = false,
}: QuerySettingsFieldsProps) => <TransmissionsMetricsField control={control} disabled={disabled} />;

export const ApiReadingsDeviceGroupsQueryForm: FC<QuerySettingsFieldsProps> = ({
  control,
  disabled = false,
  optionSources,
  timespanType,
  handleTimespanTypeChange,
}: QuerySettingsFieldsProps) => (
  <>
    <QuerySection
      title={t('dashboard_device_groups', 'Device Groups')}
      description={t(
        'dashboard_device_groups_description',
        'Read data from one or more device groups.'
      )}
      tourId="query-device-groups"
    >
      <QueryMultiSelectField
        control={control}
        name={'settings.query.deviceGroupIds' as WidgetFieldPath}
        label={t('dashboard_device_groups', 'Device Groups')}
        tourId="query-device-groups-field"
        options={optionSources.deviceGroupOptions}
        loading={optionSources.deviceGroupOptionsLoading}
        disabled={disabled}
        rules={requiredArrayRules(
          t('dashboard_select_one_device_group_required', 'Select at least one device group')
        )}
      />
    </QuerySection>
    <SharedTailFields
      control={control}
      disabled={disabled}
      timespanType={timespanType}
      handleTimespanTypeChange={handleTimespanTypeChange}
    />
  </>
);

export const ApiReadingsDeviceSensorListQueryForm: FC<QuerySettingsFieldsProps> = ({
  control,
  disabled = false,
  optionSources,
  timespanType,
  handleTimespanTypeChange,
}: QuerySettingsFieldsProps) => (
  <>
    <DeviceSensorRowsField control={control} optionSources={optionSources} disabled={disabled} />
    <SharedTailFields
      control={control}
      disabled={disabled}
      timespanType={timespanType}
      handleTimespanTypeChange={handleTimespanTypeChange}
    />
  </>
);

export const ApiReadingsDeviceSensorTypeListQueryForm: FC<QuerySettingsFieldsProps> = ({
  control,
  disabled = false,
  optionSources,
  timespanType,
  handleTimespanTypeChange,
}: QuerySettingsFieldsProps) => (
  <>
    <DeviceSensorTypeRowsField
      control={control}
      optionSources={optionSources}
      disabled={disabled}
    />
    <SharedTailFields
      control={control}
      disabled={disabled}
      timespanType={timespanType}
      handleTimespanTypeChange={handleTimespanTypeChange}
    />
  </>
);

export const ApiReadingsMixedQueryForm: FC<QuerySettingsFieldsProps> = ({
  control,
  disabled = false,
  optionSources,
  timespanType,
  handleTimespanTypeChange,
}: QuerySettingsFieldsProps) => (
  <>
    <QuerySection
      title={t('dashboard_device_groups', 'Device Groups')}
      description={t(
        'dashboard_device_groups_optional_description',
        'Optional group sources to include in the mixed query.'
      )}
      tourId="query-device-groups"
    >
      <QueryMultiSelectField
        control={control}
        name={'settings.query.deviceGroupIds' as WidgetFieldPath}
        label={t('dashboard_device_groups', 'Device Groups')}
        tourId="query-device-groups-field"
        options={optionSources.deviceGroupOptions}
        loading={optionSources.deviceGroupOptionsLoading}
        disabled={disabled}
        emptyText={t('dashboard_no_groups_selected', 'No groups selected.')}
      />
    </QuerySection>
    <DeviceSensorRowsField
      control={control}
      optionSources={optionSources}
      disabled={disabled}
      optional
    />
    <DeviceSensorTypeRowsField
      control={control}
      optionSources={optionSources}
      disabled={disabled}
      optional
    />
    <SharedTailFields
      control={control}
      disabled={disabled}
      timespanType={timespanType}
      handleTimespanTypeChange={handleTimespanTypeChange}
    />
  </>
);

const OptionMultiSelect: FC<{
  values: string[];
  label: string;
  options: QuerySelectOption[];
  disabled?: boolean;
  loading?: boolean;
  tourId?: string;
  onChange: (values: string[]) => void;
}> = ({
  values,
  label,
  options,
  disabled = false,
  loading = false,
  tourId,
  onChange,
}: {
  values: string[];
  label: string;
  options: QuerySelectOption[];
  disabled?: boolean;
  loading?: boolean;
  tourId?: string;
  onChange: (values: string[]) => void;
}) => (
  <div data-dashboard-tour={tourId}>
    <Autocomplete<QuerySelectOption, true, false, false>
      multiple
      value={values.map((value: string): QuerySelectOption => {
        return getOptionForValue(value, options) as QuerySelectOption;
      })}
      options={options}
      filterOptions={filterOptions}
      getOptionLabel={(option: QuerySelectOption): string => option.label}
      isOptionEqualToValue={(option: QuerySelectOption, selected: QuerySelectOption): boolean =>
        option.value === selected.value
      }
      disabled={disabled || loading}
      loading={loading}
      onChange={(_: React.SyntheticEvent, selected: QuerySelectOption[]): void => {
        onChange(selected.map((option: QuerySelectOption): string => option.value));
      }}
      renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
        <TextField {...params} label={label} size="small" />
      )}
    />
  </div>
);

const SharedTailFields: FC<SharedTailFieldsProps> = ({
  control,
  disabled = false,
  timespanType,
  handleTimespanTypeChange,
}: SharedTailFieldsProps) => (
  <>
    <PreferFlowField control={control} disabled={disabled} />
    <TimespanFields
      control={control}
      disabled={disabled}
      timespanType={timespanType}
      handleTimespanTypeChange={handleTimespanTypeChange}
    />
  </>
);

const DeviceSensorRowsField: FC<DeviceSensorRowsFieldProps> = ({
  control,
  optionSources,
  disabled = false,
  optional = false,
}: DeviceSensorRowsFieldProps) => (
  <Controller
    name={'settings.query.deviceSensors' as WidgetFieldPath}
    control={control}
    rules={requiredArrayRules(
      t('dashboard_add_device_sensor_required', 'Add at least one device sensor'),
      optional
    )}
    render={({
      field,
      fieldState,
    }: {
      field: ControllerRenderProps<MutateWidgetPayload, WidgetFieldPath>;
      fieldState: ControllerFieldState;
    }): JSX.Element => {
      const rows = (Array.isArray(field.value) ? field.value : []) as DeviceSensor[];

      const updateRow = (
        index: number,
        key: keyof DeviceSensor,
        value: DeviceSensor[keyof DeviceSensor]
      ): void => {
        field.onChange(
          rows.map((row: DeviceSensor, rowIndex: number): DeviceSensor => {
            if (rowIndex !== index) {
              return row;
            }

            return {
              ...row,
              [key]: value,
            };
          })
        );
      };

      return (
        <QuerySection
          title={t('dashboard_device_sensors', 'Device Sensors')}
          tourId="query-device-sensor-list"
          description={
            optional
              ? t(
                  'dashboard_device_sensors_optional_description',
                  'Optional pairs of device numbers and sensor indexes.'
                )
              : t(
                  'dashboard_device_sensors_description',
                  'Select the device and sensor index pairs this widget should read.'
                )
          }
        >
          <div className="flex flex-col gap-3">
            {rows.map((row: DeviceSensor, index: number) => (
              <div
                key={`${row.deviceNumber || 'device'}-${row.sensorIndex || 'sensor'}-${index}`}
                className="grid grid-cols-[1fr_auto] gap-2 rounded-md border border-solid border-gray-200 p-2 md:grid-cols-[1fr_1fr_auto]"
              >
                <OptionSelect
                  value={row.deviceNumber}
                  label={t('drawer_device', 'Device')}
                  tourId="query-device-sensor-device"
                  options={optionSources.singleDeviceOptions}
                  loading={optionSources.deviceOptionsLoading}
                  disabled={disabled}
                  onChange={(value: string): void => {
                    updateRow(index, 'deviceNumber', value);
                  }}
                />
                <OptionSelect
                  value={row.sensorIndex}
                  label={t('sensor', 'Sensor')}
                  tourId="query-device-sensor-sensor"
                  options={optionSources.sensorIndexOptions}
                  disabled={disabled || !row.deviceNumber}
                  onChange={(value: string): void => {
                    updateRow(index, 'sensorIndex', value);
                  }}
                />
                <DeleteQueryRowButton
                  label={t('dashboard_remove_device_sensor', 'Remove device sensor')}
                  disabled={disabled}
                  onClick={(): void => {
                    field.onChange(
                      rows.filter(
                        (_: DeviceSensor, rowIndex: number): boolean => rowIndex !== index
                      )
                    );
                  }}
                />
              </div>
            ))}

            <AddQueryRowButton
              label={t('dashboard_add_device_sensor', 'Add Device Sensor')}
              disabled={disabled}
              tourId="query-device-sensor-add"
              onClick={(): void => {
                field.onChange([...rows, { deviceNumber: '', sensorIndex: '0' }]);
              }}
            />

            {fieldState.error && (
              <Typography color="error" variant="caption">
                {fieldState.error.message}
              </Typography>
            )}
          </div>
        </QuerySection>
      );
    }}
  />
);
